import React, { useState, useEffect, useMemo } from 'react';
import { useImmer } from 'use-immer';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import RestHelper from 'utils/RestHelper';
import { DownloadOutlined } from '@ant-design/icons';
import { Row, Col, Button, Modal, Table, Switch, Card, Input, Spin, DatePicker, Tooltip, Radio, Tag, message } from 'antd';
import moment from 'moment';
import XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n.js';
import { PLATFORM_ID } from 'utils/constants';
import { convertIosModelToProductName } from 'utils/model-convert/ios-model-convert';
import { useZhEn } from 'utils/react-hooks/useZhEn';
import { ze } from 'utils/zhEn';
import IssueCrashFilterEx from 'components/commons/IssueCrashFilter/IssueCrashFilterEx';
import { FieldName, QueryType } from 'components/commons/IssueCrashFilter/IssueCrashFilterExUtil';
import {
  isAndroidOrHarmony,
  isMobile,
  isPcOrLinux,
  isPc,
  isAndroid,
  isIos,
  isLinux,
  isMac,
  isWechatMiniProgram
} from 'utils/platform';
import { BillingTeamType } from 'utils/constants/billing';
import CsDownloadButton from 'components/commons/CsDownloadButton/CsDownloadButton';
import IssueChartDownloadFieldSelectModal from 'components/exception/issue/IssueChartDownloadFieldSelectModal';
import { XlsxUtil } from 'utils/xlsx-util';
import { COLOR_LIST } from 'utils/constants/chart-options';
import { decodeCsSdkEncodedValue } from 'utils/helper';
import { IssueAggregateType } from 'utils/constants/issue-aggregate-type';
import { formatBytes } from 'utils/format/file';
import { getCrashStackSearchTooltipDom } from 'utils/crash-search/convert-search-content';
import WrappedTipsIcon from 'components/antd-extension/WrappedTipsIcon';
import { CS_STYLES } from 'utils/constants/style-constants';
import { EnvUtil } from 'utils/env-util';
import { VmTypeInt, VmTypeUtil } from 'utils/constants/vm-type';
import { isNullish } from 'utils/nullish';
import { TopIssueUtils } from 'components/exception/ProductSummaryPage/TopIssueList/TopIssueList';
import {ServerAppSettings} from 'utils/server-app-settings';
import {
  buildBaselineRatioMap,
  evaluateDimensionAnomaly,
  isBlankPieSlice,
  resolveBaselineQueryContext,
} from 'components/exception/issue/IssueHead/IssueOutlierBar';

/**
 * @param {{ total: Number, bucketList: { key: String, docCount: Number, notInLimitedBuckets: boolean }[] }} issueAggregateSingleResult
 * @param {Number} aggregateType
 * @param {{ bucketKeyList: String[], bucketKeyDict: Object }} bucketKeyExtraInfo
 */
function refineIssueAggregateSingleResult(issueAggregateSingleResult, aggregateType, bucketKeyExtraInfo = {}) {
  const { total } = issueAggregateSingleResult;
  let { bucketList } = issueAggregateSingleResult;
  const { bucketKeyList, bucketKeyDict, fieldKey } = bucketKeyExtraInfo;
  // 如果提供了bucketKeyList, 那么用对应指定的key覆盖原来bucket的key
  // 如果提供了bucketKeyDictObj，根据字典来替换key
  if (bucketKeyList) {
    bucketList = bucketList.map((x, i) => ({ ...x, key: bucketKeyList[i] }));
  } else if (bucketKeyDict) {
    bucketList = bucketList.map(x => ({ ...x, key: bucketKeyDict[x.key] || x.key }));
  }
  // 过滤掉 <= 0 的项；若是离散饼图（aggregateType === 0），把空字符串或 0 GiB 的无效桶一并归入无数据
  bucketList = bucketList.filter((x) => {
    if (!(x.docCount > 0)) return false;
    if (aggregateType === 0 && !x.notInLimitedBuckets) {
      const decoded = decodeCsSdkEncodedValue(x.key);
      if (isBlankPieSlice(fieldKey, decoded, x.key)) {
        return false;
      }
    }
    return true;
  });
  // 有数据的样本条数（已排除空串与 0 GiB 桶）
  const totalWithData = bucketList.reduce((acc, x) => acc + x.docCount, 0);

  if (aggregateType === 0) {
    // 按照数量大小排序，其他的（notInLimitedBuckets = true）排在最后面
    bucketList.sort((a, b) => a.notInLimitedBuckets - b.notInLimitedBuckets || b.docCount - a.docCount);
    // 只保留TOP N，TOP N以下的都聚合成“其他”
    // N=10或20，判断方法是如果N=10的时候“其他”占20%以下（和total对比），则取N=10。否则取N=20
    const TOP_N_VALUE_CANDIDATES = [10, 20];
    const TOP_N_PERCENTAGE_THRESHOLD = 0.8;
    const countTopN = (n) => {
      return bucketList
        .filter((x, i) => i < n && !x.notInLimitedBuckets)
        .reduce((acc, x) => acc + x.docCount, 0);
    };
    const topNValue = TOP_N_VALUE_CANDIDATES.find((n) => {
      return totalWithData > 0 && countTopN(n) / totalWithData > TOP_N_PERCENTAGE_THRESHOLD;
    }) || TOP_N_VALUE_CANDIDATES[TOP_N_VALUE_CANDIDATES.length - 1];

    const countBelowTopN = totalWithData - countTopN(topNValue);
    bucketList = bucketList.map((x, i) => ({
      ...x,
      visibleInChart: i < topNValue && !x.notInLimitedBuckets,
      visibleInDownload: true,
    }));
    if (countBelowTopN > 0) {
      bucketList.push({
        key: ze('其他', 'Others'),
        docCount: countBelowTopN,
        visibleInChart: true,
        visibleInDownload: false,
        isShowAsOthers: true,
      });
    }
  } else {
    bucketList = bucketList.map(x => ({ ...x, visibleInChart: true, visibleInDownload: true }));
  }
  // 添加无数据的项
  const noDataCount = total - totalWithData;
  if (aggregateType === 0 && totalWithData === 0) {
    bucketList.push({
      key: ze('无数据', 'No Data'),
      docCount: noDataCount,
      isNoData: true,
      visibleInChart: true,
      visibleInDownload: false,
    });
  }
  return { ...issueAggregateSingleResult, total, noDataCount, bucketList };
}

/**
 *  @param {{key: String, label: String, total: Number, aggregateType: Number, valueList: {name, count, isNoData}[]}} option
 */
function refineHistogramData(ze, option) {
  const { valueList } = option;
  // 把百分比合并到0, 10, 20, ... 90的十分位
  const reducedValueMap = valueList.reduce((acc, x) => {
    const percentage = Math.round(x.name * 100);
    let xValue = Math.floor(percentage / 10) * 10;
    if (xValue > 90) {
      xValue = 90;
    }
    acc.set(xValue, (acc.get(xValue) | 0) + x.count);
    return acc;
  }, new Map());

  const totalWithData = valueList.reduce((acc, x) => acc + x.count, 0);

  return [...Array(10).keys()]
    .map(x => x * 10)
    .map(x => ({
      value: [x + 5, (reducedValueMap.get(x) / totalWithData) || 0],
      name: `${x} ~ ${x + 10}%${ze('可用', ' Available')}`,
      reportCount: (reducedValueMap.get(x) || 0),
    }));
}

export const DeviceStatsUtil = Object.freeze({
  refineIssueAggregateSingleResult,
});

/**
 * 设备信息卡片子页面
 * @param {*} props
 */
const DeviceStats = (props) => {
  const {
    appId,
    platformId,
    issueHash,
    externalSearchConditionGroup,
    externalSearchTrigger,
    insightEchartsDrilldown,
    reduxState,
  } = props;

  const serverAppSettings = useMemo(() => reduxState.app.get('appIdToServerAppSettings')?.[appId] || {},
    [reduxState.app, appId]);
  // 是否是先锋云游戏项目
  const isAppGameMatrix = !!serverAppSettings[ServerAppSettings.keys.isAppGameMatrix];
  const enableIssueOutlierAndVariants = !!serverAppSettings[ServerAppSettings.keys.enableIssueOutlierAndVariants];

  const { t } = useTranslation();
  const { ze } = useZhEn();
  const pid = platformId;
  const teamType = reduxState.app.get('current').get('teamType');
  const isNotCommercialApp = teamType && teamType !== BillingTeamType.COMMERCIAL;
  const issueObj = reduxState.issue.toJS();
  const versionOptions = issueObj.selectOptions.version.options.filter(x => x.value && x.value !== 'all');

  const { crashDoc } = issueObj.current || {};
  const { esMap } = crashDoc || {};
  const {
    totalGpuMemGib,
  } = esMap || {};
  const gpuMemoryReported = totalGpuMemGib > 0; // 采集gpu是新SDK的功能，旧SDK上报的数据不显示对应饼图

  const isFreeStorageAvailable = isAndroid(pid) || isIos(pid);
  const isFreeSdAvailable = !EnvUtil.isChinaEnv() && isAndroid(pid);
  const isFreeMemAvailable = isMobile(pid);
  const isFreeProcessMemAvailable = isIos(pid, appId);
  const isFreeGpuMemAvailable = (isPc(pid, appId) || isLinux(pid, appId)) && gpuMemoryReported;
  const isMemoryAvailable = isPc(pid, appId) || isLinux(pid, appId);

  const percentilesAggPercents = [1, 5, 10, 25, 50, 75, 90];
  function makePercentTableKey(percent) {
    return 'P' + String(Number(percent));
  }

  function makeFieldFilterGt0(field) {
    return {
      conditions: [{
        field,
        queryType: QueryType.RANGE,
        gt: 0,
      }],
    };
  }

  const fields = [{
    available: isMobile(pid) || isWechatMiniProgram(pid),
    name: 'hardware',
    label: t('hardwareInfo.机型设备'),
    aggregateType: 0,
    bucketListHandler: (o) => {
      return o.bucketList.map(x => ({
        ...x,
        name: convertIosModelToProductName(decodeCsSdkEncodedValue(x.key)),
        count: x.docCount,
      }))
    },
  }, {
    available: true,
    name: isPcOrLinux(pid) ? 'osName' : 'os',
    label: t('hardwareInfo.系统版本'),
    aggregateType: 0,
    bucketListHandler: (o) => {
      return o.bucketList.map(x => ({
        ...x,
        name: decodeCsSdkEncodedValue(x.key),
        count: x.docCount,
      }))
    },
  }, {
    available: isAndroid(pid),
    name: 'brand',
    label: t('hardwareInfo.厂商'),
    aggregateType: 0,
  }, {
    available: isAndroid(pid),
    name: 'cpuType',
    label: t('REPORTDETAIL.cpu'),
    aggregateType: 0,
  }, {
    available: isMac(pid) || isPc(pid, appId) || isLinux(pid, appId),
    name: 'cpu',
    label: t('issueCrashFilterKey.cpu'),
    aggregateType: 0,
  }, {
    available: isAndroidOrHarmony(pid),
    name: 'appBit',
    label: t('issueCrashFilterKey.appBit'),
    aggregateType: 0,
    bucketKeyDict: {
      0: ze('未知', 'Unknown'),
      32: ze('32位', '32 bit'),
      64: ze('64位', '64 bit'),
    },
  }, {
    available: (isNotCommercialApp && isAndroid(pid))
      || isPc(pid, appId)
      || isLinux(pid, appId),
    name: 'gpu',
    label: 'GPU',
    aggregateType: 0,
  }, {
    available: isAndroid(pid),
    name: 'reservedMapFlattened.soc',
    label: 'SoC',
    aggregateType: 0,
  }, {
    // iOS MetricKit：启动崩溃标记（ES 扁平字段 reservedMapFlattened.startupCrash）
    available: isIos(pid),
    name: 'reservedMapFlattened.startupCrash',
    label: ze('是否启动崩溃', 'Startup Crash'),
    aggregateType: 0,
    bucketKeyDict: {
      true: ze('是', 'Yes'),
      false: ze('否', 'No'),
    },
  }, {
    // iOS MetricKit 卡死：启动卡死标记（ES 扁平字段 reservedMapFlattened.startupHang）
    available: isIos(pid),
    name: 'reservedMapFlattened.startupHang',
    label: ze('是否是启动卡死', 'Startup Hang'),
    aggregateType: 0,
    bucketKeyDict: {
      true: ze('是', 'Yes'),
      false: ze('否', 'No'),
    },
  }, {
    available: isPc(pid, appId) || isLinux(pid, appId),
    name: 'displayVersion',
    label: t('issueCrashFilterKey.gpuDriverVersion'),
    aggregateType: 0,
  }, {
    available: (isPc(pid, appId) || isLinux(pid, appId)) && gpuMemoryReported,
    name: 'totalGpuMemGib',
    label: ze('GPU内存', 'GPU Memory'),
    aggregateType: 0,
    bucketListHandler: (o) => {
      return o.bucketList.map(x => ({
        ...x,
        name: (!x.isShowAsOthers && !x.isNoData) ? `${Number(x.key)} GiB` : x.key,
        count: x.docCount,
      }));
    },
  }, {
    available: isMobile(pid),
    name: 'ram',
    label: t('REPORTDETAIL.memSize'),
    aggregateType: 0,
    bucketListHandler: (o) => {
      return o.bucketList.map(x => ({
        ...x,
        name: (!x.isShowAsOthers && !x.isNoData) ? `${Number(x.key)} GiB` : x.key,
        count: x.docCount,
      }));
    },
  }, {
    available: isAndroidOrHarmony(platformId),
    name: 'isRooted',
    label: t('hardwareInfo.是否ROOT'),
    aggregateType: 0,
    bucketKeyDict: {
      false: ze('否', 'No'),
      true: ze('是', 'Yes'),
    },
  }, {
    available: isMobile(platformId),
    name: 'crashDataType',
    label: t('hardwareInfo.是否是系统退出'),
    aggregateType: 0,
    bucketKeyDict: {
      false: ze('否', 'No'),
      true: ze('是', 'Yes'),
    },
  }, {
    available: isAndroidOrHarmony(platformId),
    name: FieldName.vmType,
    label: t('issueCrashFilterKey.vmType'),
    aggregateType: 0,
    bucketKeyDict: {
      [VmTypeInt.ALL]: ze('未知', 'Unknown'),
      [VmTypeInt.REAL_DEVICE]: t(`vmTypeOptions.${VmTypeInt.REAL_DEVICE}`),
      [VmTypeInt.EMULATOR]: t(`vmTypeOptions.${VmTypeInt.EMULATOR}`),
      [VmTypeInt.CLOUD_GAMING]: t(`vmTypeOptions.${VmTypeInt.CLOUD_GAMING}`),
    },
  }, {
    available: isAndroid(platformId),
    name: FieldName.vmDeviceBrand,
    label: t('issueCrashFilterKey.vmDeviceBrand'),
    aggregateType: 0,
    // 只统计模拟器上报数据
    fieldFilter: {
      conditions: [{
        field: FieldName.vmType,
        queryType: QueryType.TERM,
        term: VmTypeInt.EMULATOR,
      }],
    },
    bucketKeyDict: VmTypeUtil.getVmDeviceBrandBucketKeyDict(),
  }, {
    available: isAppGameMatrix,
    name: 'K#gmHardware',
    label: ze('（云游戏）平台类型', '(Cloud Gaming) Platform Type'),
    aggregateType: 0,
    isSelfDefinedKeyValue: true,
  }, {
    available: isAppGameMatrix,
    name: 'K#gmCluster',
    label: ze('（云游戏）平台集群', '(Cloud Gaming) Platform Cluster'),
    aggregateType: 0,
    isSelfDefinedKeyValue: true,
  }, {
    available: isAppGameMatrix,
    name: 'K#gmGameId',
    label: ze('（云游戏）Game ID', '(Cloud Gaming) Game ID'),
    aggregateType: 0,
    isSelfDefinedKeyValue: true,
  }, {
    available: isAppGameMatrix,
    name: 'K#gmGameCluster',
    label: ze('（云游戏）游戏集群', '(Cloud Gaming) Game Cluster'),
    aggregateType: 0,
    isSelfDefinedKeyValue: true,
  }, {
    available: isFreeStorageAvailable,
    name: 'freeStorageR',
    label: t('hardwareInfo.存储可用占比'),
    aggregateType: IssueAggregateType.HISTOGRAM,
    histogramInterval: 0.01,
    fieldFilter: makeFieldFilterGt0('freeStorage'),
  }, {
    available: isFreeStorageAvailable,
    name: 'freeStorage',
    aggregateType: IssueAggregateType.PERCENTILES,
    percents: percentilesAggPercents,
    fieldFilter: makeFieldFilterGt0('freeStorage'),
  }, {
    available: isFreeSdAvailable,
    name: 'freeSdR',
    label: t('hardwareInfo.SD卡可用占比'),
    aggregateType: IssueAggregateType.HISTOGRAM,
    histogramInterval: 0.01,
    fieldFilter: makeFieldFilterGt0('freeSdCard'),
  }, {
    available: isFreeSdAvailable,
    name: 'freeSdCard',
    aggregateType: IssueAggregateType.PERCENTILES,
    percents: percentilesAggPercents,
    fieldFilter: makeFieldFilterGt0('freeSdCard'),
  }, {
    available: isFreeProcessMemAvailable,
    name: 'freeProcessMemR',
    label: t('hardwareInfo.进程内存可用占比'),
    aggregateType: IssueAggregateType.HISTOGRAM,
    histogramInterval: 0.01,
    fieldFilter: makeFieldFilterGt0('freeProcessMem'),
  }, {
    available: isFreeProcessMemAvailable,
    name: 'freeProcessMem',
    aggregateType: IssueAggregateType.PERCENTILES,
    percents: percentilesAggPercents,
    fieldFilter: makeFieldFilterGt0('freeProcessMem'),
  }, {
    available: isFreeMemAvailable,
    name: 'freeMemR',
    label: t('hardwareInfo.内存可用占比'),
    aggregateType: IssueAggregateType.HISTOGRAM,
    histogramInterval: 0.01,
    fieldFilter: makeFieldFilterGt0('freeMem'),
  }, {
    available: isFreeMemAvailable || isPc(pid, appId) || isLinux(pid, appId),
    name: 'freeMem',
    aggregateType: IssueAggregateType.PERCENTILES,
    percents: percentilesAggPercents,
    fieldFilter: makeFieldFilterGt0('freeMem'),
  }, {
    available: isFreeGpuMemAvailable,
    name: 'freeGpuMemR',
    label: t('hardwareInfo.GPU内存可用占比'),
    aggregateType: IssueAggregateType.HISTOGRAM,
    histogramInterval: 0.01,
    fieldFilter: makeFieldFilterGt0('freeGpuMem'),
  }, {
    available: isFreeGpuMemAvailable,
    name: 'freeGpuMem',
    aggregateType: IssueAggregateType.PERCENTILES,
    percents: percentilesAggPercents,
    fieldFilter: makeFieldFilterGt0('freeGpuMem'),
  }, {
    available: isMemoryAvailable,
    name: 'freeVirtualMemory',
    aggregateType: IssueAggregateType.PERCENTILES,
    percents: percentilesAggPercents,
    fieldFilter: makeFieldFilterGt0('freeVirtualMemory'),
  }].filter(x => x.available).map(x => ({
    isSelfDefinedKeyValue: false,
    ...x,
    termsSizeLimit: 300,
  }));

  const percentilesDimensionKeyToLabel = {
    freeStorage: t('TRACKDATATAB.storageAvaible'),
    freeSdCard: t('TRACKDATATAB.sdAvaible'),
    freeMem: t('TRACKDATATAB.memoryAvaible'),
    freeProcessMem: t('TRACKDATATAB.processMemoryAvailable'),
    freeGpuMem: t('TRACKDATATAB.GPU剩余可用内存大小'),
    freeVirtualMemory: t('TRACKDATATAB.可用虚拟内存大小'),
  };

  const percentilesTableColumns = [
    {
      title: ze('指标', 'Dimension'),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => percentilesDimensionKeyToLabel[text] || text,
    },
    ...percentilesAggPercents.map(percent => {
      const percentKey = makePercentTableKey(percent);
      return {
        title: <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
          { percentKey }
          <Tooltip
            title={ze(`上报的第${percent}百分位数，表示有${percent}%的上报数据小于该值。`, `P${percent} indicates ${percent}% of reports are below the specified value.`)}
          ><WrappedTipsIcon style={{ color: CS_STYLES.SUB_TEXT_COLOR }}/></Tooltip>
        </div>,
        dataIndex: percentKey,
        key: percentKey,
        align: 'right',
        render: (text, record) => {
          const v = Number(record[percentKey]);
          return Number.isFinite(v) ? formatBytes(v) : '-';
        },
      };
    }),
    {
      title: <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
        { ze('有效异常次数', 'Reports With Valid Data') }
        <Tooltip
          title={ze('部分上报由于异常中断等原因未采集到数据。', 'Data of some reports are not collected due to interruption or other reasons.')}
        ><WrappedTipsIcon style={{ color: CS_STYLES.SUB_TEXT_COLOR }}/></Tooltip>
      </div>,
      dataIndex: 'fieldFilterTotal',
      key: 'fieldFilterTotal',
      align: 'right',
    },
    {
      title: ze('有效次数占比', '% of Reports With Valid Data'),
      dataIndex: 'fieldFilterTotalRatio',
      key: 'fieldFilterTotalRatio',
      align: 'right',
      render: (text, record) => {
        const { total, fieldFilterTotal } = record;
        return total ? `${(fieldFilterTotal / total * 100).toFixed(2)}%` : '-';
      },
    },
  ];

  const downloadableFieldOptions = useMemo(() => {
    return fields
      .filter(x => x.aggregateType !== 1 && x.aggregateType !== IssueAggregateType.PERCENTILES)
      .map(x => ({ label: x.label, value: x.name }));
  }, [fields]);

  const [state, updateState] = useImmer({
    loading: true,
    searchConditionGroup: {
      conditions: [
        { field: FieldName.crashUploadTime, queryType: QueryType.RANGE_RELATIVE_DATETIME, gte: 7 * 86400 * 1000 },
      ],
    },
    chartDataList: [],
    percentilesTableData: [],
  });
  const { searchConditionGroup } = state;

  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [sortMode, setSortMode] = useState('anomaly');

  const [searchTriggerCount, setSearchTriggerCount] = useState(0);

  // 拉取设备信息统计数据
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!state.loading) {
        updateState((draft) => {
          draft.loading = true;
        });
      }

      const activeSearchGroup = externalSearchConditionGroup || searchConditionGroup;
      const baseCtx = resolveBaselineQueryContext(activeSearchGroup);
      const fetchBaseline = async (field) => {
        try {
          const baseRsp = await RestHelper.post('/redir/api/statResource/fetchFieldTopStats', {
            appId,
            pid: Number(platformId) || 1,
            platformId: Number(platformId) || 1,
            mergeMultipleVersionsWithInaccurateResult:
              baseCtx.mergeMultipleVersionsWithInaccurateResult,
            version: baseCtx.version,
            versionList: baseCtx.versionList,
            minDate: baseCtx.minDate,
            maxDate: baseCtx.maxDate,
            mergeMultipleDatesWithInaccurateResult: baseCtx.mergeMultipleDatesWithInaccurateResult,
            type: 'crash',
            limit: 30,
            field,
            sortByException: false,
            countryList: [],
            vmTypeList: [0],
            needQueryFieldRollup: true,
          });
          return buildBaselineRatioMap(baseRsp?.json?.data || []);
        } catch (e) {
          return {};
        }
      };

      const [rsp, osBaselineMap, modelBaselineMap, gpuBaselineMap, cpuBaselineMap, driverBaselineMap] = await Promise.all([
        RestHelper.post('/api/issue/queryIssueAggregate', {
          appId,
          platformId,
          issueHash,
          customFields: fields,
          searchConditionGroup: activeSearchGroup,
        }),
        enableIssueOutlierAndVariants ? fetchBaseline('osVersion') : Promise.resolve({}),
        enableIssueOutlierAndVariants && isMobile(platformId) ? fetchBaseline('model') : Promise.resolve({}),
        enableIssueOutlierAndVariants && (isPc(platformId, appId) || isLinux(platformId, appId)) ? fetchBaseline('gpu') : Promise.resolve({}),
        enableIssueOutlierAndVariants && (isPc(platformId, appId) || isMac(platformId) || isLinux(platformId, appId)) ? fetchBaseline('cpu') : Promise.resolve({}),
        enableIssueOutlierAndVariants && (isPc(platformId, appId) || isLinux(platformId, appId)) ? fetchBaseline('gpuDriverVersion') : Promise.resolve({}),
      ]);
      if (cancelled) return;
      const chartDataList = rsp.json.data
        .map((x, i) => refineIssueAggregateSingleResult(x, fields[i].aggregateType, { bucketKeyDict: fields[i].bucketKeyDict, fieldKey: fields[i].name }))
        .map((x, i) => {
          const valueList = fields[i].bucketListHandler ? fields[i].bucketListHandler(x) : x.bucketList.map((y, j) => {
            return {
              ...y,
              name: y.key,
              count: y.docCount,
            };
          });
          const fieldKey = fields[i].name;
          const baseMap =
            fieldKey === 'os' || fieldKey === 'osName'
              ? osBaselineMap
              : fieldKey === 'hardware'
              ? modelBaselineMap
              : fieldKey === 'gpu'
              ? gpuBaselineMap
              : fieldKey === 'cpu'
              ? cpuBaselineMap
              : fieldKey === 'displayVersion'
              ? driverBaselineMap
              : {};
          const anomaly =
            enableIssueOutlierAndVariants && fields[i].aggregateType === 0
              ? evaluateDimensionAnomaly({
                  fieldKey,
                  fieldLabel: fields[i].label,
                  total: x.total,
                  noDataCount: x.noDataCount,
                  valueList,
                  baselineMap: baseMap,
                })
              : null;
          return {
            key: fieldKey,
            label: fields[i].label,
            aggregateType: fields[i].aggregateType,
            total: x.total,
            noDataCount: x.noDataCount,
            valueList,
            anomaly,
            originalIndex: i,
          };
        })
        .filter((x, i) => fields[i].aggregateType !== IssueAggregateType.PERCENTILES);
      const percentilesTableData = rsp.json.data
        .filter((x, i) => fields[i].aggregateType === IssueAggregateType.PERCENTILES)
        .map(x => {
          const { bucketList, total, name, fieldFilterTotal } = x;
          const percentKeyToValue = Object.fromEntries(bucketList.map(y => [makePercentTableKey(y.key), y.value]));
          return {
            name,
            total,
            fieldFilterTotal,
            ...percentKeyToValue,
          };
        });

      updateState((draft) => {
        draft.chartDataList = chartDataList;
        draft.percentilesTableData = percentilesTableData;
        draft.loading = false;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [searchTriggerCount, externalSearchTrigger, enableIssueOutlierAndVariants]);

  function onClickDownloadFields(fieldKeyList) {
    const downloadDataList = state.chartDataList.filter(x => fieldKeyList.includes(x.key));

    const title = `${t('hardwareInfo.全量数据')}.xlsx`;
    const dataObjectList = downloadDataList.map((x) => {
      const { label, total, valueList } = x;
      const header = [`${label}`, t('hardwareInfo.异常次数'), t('hardwareInfo.次数占比')];

      const body = valueList.filter(x => x.visibleInDownload).map(x => {
        return [
          x.name,
          x.count,
          `${(x.count / total * 100).toFixed(2)} %`,
        ];
      });

      const totalLabel = ze('总异常数', 'Total Reports');
      const data = [header, ...body, [totalLabel, total]];
      return { sheetName: label, data };
    });
    XlsxUtil.createMultipleSheetsExcelAndDownload(title, dataObjectList);
  }

  const handleClick = (event,option) => {
    const {name, isNoData, isShowAsOthers} = event.data || {};
    const {key, valueList, aggregateType} = option;
    const aggList = fields.reduce((acc, item) => {
      acc[item.name] = item.name;
      return acc;
    }, {});
    let value = null;
    let newDataList = [];

    if(isNoData || isShowAsOthers){
      return;
    }

    if (aggList[key] === 'hardware') {
      const fieldConfig = valueList.find(field => field.name === name);
      const { key } = fieldConfig;
      //此处仅限于ios,等后台更换ios机型Modal大小写的时候需要替换
      const termName = decodeURIComponent(key)
        .replace(/\b(iphone)(\d*|,.*)?\b/gi, 'iPhone$2')
        .replace(/\b(ipad)(\d*|,.*)?\b/gi, 'iPad$2');
      value = {
        queryType: QueryType.TERMS,
        terms: isIos(pid) ? [termName] : [key],
        field: isIos(pid) ? FieldName.iosModel : FieldName.androidModel
      };
    }  else if (aggList[key] === 'osName' || aggList[key] === 'os') {
      value = {
        queryType: QueryType.TERM,
        not:false,
        term: name,
        field: FieldName.osVersion
      };
    } else if(aggList[key] === 'appBit') {
      const fieldConfig = fields.find(field => field.name === key);
      let termName = name;
      if (fieldConfig && fieldConfig.bucketKeyDict) {
        const matchingEntry = Object.entries(fieldConfig.bucketKeyDict).find(
          ([, dictValue]) => dictValue === name
        );
        if (matchingEntry) {
          termName = isNaN(matchingEntry[0]) ? matchingEntry[0] : Number(matchingEntry[0]);
        }
      }
      value = {
        queryType: QueryType.TERMS,
        terms: [termName],
        field: FieldName.appBit,
      };
    } else if (aggList[key] === 'gpu') {
      value = {
        queryType: QueryType.TERM_CONTAINS,
        not: false,
        term: name,
        field: FieldName.gpu,
      };
    } else if (aggList[key] === 'cpu') {
      value = {
        queryType: QueryType.TERM_CONTAINS,
        not: false,
        term: name,
        field: FieldName.cpu,
      };
    } else if (aggList[key] === 'crashDataType') {
      const fieldConfig = fields.find(field => field.name === key);
      let termName = name;
      if (fieldConfig && fieldConfig.bucketKeyDict) {
        const matchingEntry = Object.entries(fieldConfig.bucketKeyDict).find(
          ([, dictValue]) => dictValue === name
        );
        if (matchingEntry) {
          termName = matchingEntry[0] === 'true' ? 'ONLY_SYSTEM_EXIT' : 'ONLY_NOT_SYSTEM_EXIT';
        } else {
          termName = 'ALL'
        }
      }
      value = {
        queryType: QueryType.TERM,
        term: termName,
        field: FieldName.systemExitStatus,
      };
    } else if (aggList[key] === FieldName.vmType) {
      const fieldConfig = fields.find(field => field.name === key);
      let termName = name;
      if (fieldConfig && fieldConfig.bucketKeyDict) {
        const matchingEntry = Object.entries(fieldConfig.bucketKeyDict).find(
          ([, dictValue]) => dictValue === name
        );
        if (matchingEntry) {
          termName = isNaN(matchingEntry[0]) ? matchingEntry[0] : Number(matchingEntry[0]);
        }
      }
      value = {
        queryType: QueryType.TERMS,
        terms: [termName],
        field: FieldName.vmType,
      };
    } else if (aggList[key] === 'displayVersion') {
      value = {
        queryType: QueryType.TERM_CONTAINS,
        not: false,
        term: name,
        field: FieldName.gpuDriverVersion,
      };
    }

    if(aggregateType === 1) {
      return;
    }

    if (!value) {
      message.warning(ze('该字段不支持下钻','This field does not support drill down'));
      return;
    }

    let currentConditions = insightEchartsDrilldown
      ? [...externalSearchConditionGroup.conditions]
      : [...state.searchConditionGroup.conditions];


    if (insightEchartsDrilldown) {
      currentConditions = currentConditions.filter(
        condition => condition.field !== FieldName.exceptionCategory
      );
    }

    const updatedConditions = currentConditions.filter(
      condition => condition.field !== value.field
    );

    updatedConditions.push(value);

    if (insightEchartsDrilldown) {
      insightEchartsDrilldown(updatedConditions);
    } else {
      updateState(draft => {
        draft.searchConditionGroup.conditions = updatedConditions;
      });
      setSearchTriggerCount(searchTriggerCount + 1);
    }
  };

  return (
    <div>
      <Spin spinning={state.loading}>
        { isNullish(externalSearchConditionGroup) && <div style={{ marginBottom: '12px' }}>
          <IssueCrashFilterEx
            hideIssueFields={true}
            fieldBlacklist={['crashId', 'issueId', 'bundleId', 'deviceIdList', 'expUid', 'crashId', 'issueId', 'userId', 'exceptionTypeList', 'uploadTime', 'crashTime']}
            fixedFieldList={[FieldName.crashUploadTime]}
            platformId={platformId}
            filterOptions={{
              version: versionOptions,
            }}
            searchConditionGroup={searchConditionGroup}
            onChange={({ searchConditionGroup }) => {
              updateState(draft => {
                draft.searchConditionGroup = searchConditionGroup;
              });
            }}
            onSubmit={({ searchConditionGroup }) => {
              updateState(draft => {
                draft.searchConditionGroup = searchConditionGroup;
              });
              setSearchTriggerCount(searchTriggerCount + 1);
            }}
          />
        </div> }
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          {enableIssueOutlierAndVariants ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: CS_STYLES.SUB_TEXT_COLOR }}>
                {ze('图表排序:', 'Chart Order:')}
              </span>
              <Radio.Group
                size="small"
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value)}
              >
                <Radio.Button value="anomaly">
                  {ze('按异常显著度排序', 'Sort by Anomaly / Skew')}
                </Radio.Button>
                <Radio.Button value="default">
                  {ze('默认顺序', 'Default Order')}
                </Radio.Button>
              </Radio.Group>
            </div>
          ) : (
            <div />
          )}
          <CsDownloadButton
            disabled={downloadableFieldOptions.length === 0}
            onClick={() => setDownloadModalVisible(true)}
          />
        </div>
        <div style={{ minHeight: '100px' }}>
          <Row gutter={5}>
            {[...state.chartDataList.filter(x => x.aggregateType === 0)]
              .sort((a, b) => {
                if (!enableIssueOutlierAndVariants || sortMode !== 'anomaly') {
                  return (a.originalIndex ?? 0) - (b.originalIndex ?? 0);
                }
                const scoreA = a.anomaly?.score ?? -1;
                const scoreB = b.anomaly?.score ?? -1;
                return scoreB - scoreA || (a.originalIndex ?? 0) - (b.originalIndex ?? 0);
              })
              .map(x => (
                <Col span={8} key={x.key}><ChartContainer option={x} handleClick={handleClick}/></Col>
              ))}
          </Row>
          <Row gutter={5}>
            {state.chartDataList.filter(x => x.aggregateType !== 0).map(x => (
              <Col span={8} key={x.key}><ChartContainer option={x} handleClick={handleClick}/></Col>
            ))}
          </Row>
        </div>
        { fields.some(x => x.aggregateType === IssueAggregateType.PERCENTILES) && <div>
          <Table
            size='small'
            columns={percentilesTableColumns}
            dataSource={state.percentilesTableData}
            pagination={false}
          />
        </div> }
      </Spin>
      <IssueChartDownloadFieldSelectModal
        fieldOptions={downloadableFieldOptions}
        visible={downloadModalVisible}
        onVisibleChange={(v) => setDownloadModalVisible(v)}
        onConfirm={(v) => onClickDownloadFields(v)}
      />
    </div>
  );
};

function createExcel(data,title){
  const { utils } = XLSX;
  var ws_name = "sheet1";
  const wb= utils.book_new();
  /* make worksheet */
  var ws = utils.aoa_to_sheet(data);

  /* Add the worksheet to the workbook */
  XLSX.utils.book_append_sheet(wb, ws, ws_name);
  XLSX.writeFile(wb, title);
}

/**
 * @param {{key: String,
 *   label: String,
 *   total: Number,
 *   valueList: {name, count, isNoData}[],
 * }} option
*/
function handleDownload(option) {
  const { label, total, valueList } = option;
  const title = `${label}-${i18n.t('hardwareInfo.全量数据')}.xlsx`;
  const header = [`${label}`, i18n.t('hardwareInfo.异常次数'), i18n.t('hardwareInfo.次数占比')];

  const body = valueList.filter(x => x.visibleInDownload).map(x => {
    return [
      x.name,
      x.count,
      `${(x.count / total * 100).toFixed(2)} %`,
    ];
  });

  const totalLabel = ze('总异常数', 'Total Reports');
  const datas = [header, ...body, [totalLabel, total]];
  createExcel(datas, title);
}

/**
 *
 * @param {{option: {
 *   key: String,
 *   label: String,
 *   total: Number,
 *   noDataCount: Number,
 *   aggregateType: Number,
 *   valueList: {name, count, isNoData}[],
 * }}} props
 */
const ChartContainer = (props) => {
  const { t } = useTranslation();
  const { ze } = useZhEn();
  const { option, handleClick } = props;
  let chartOptions = {};

  let subtext = '';
  const hasDataCount = Math.max(0, Number(option.total || 0) - Number(option.noDataCount || 0));
  const hasDataPercentageFormatted = option.total > 0
    ? TopIssueUtils.formatPercentage(100 * hasDataCount / option.total, { maxDecimals: 2 })
    : '';
  let validDataText = `${t('hardwareInfo.有效异常次数')}: ${hasDataCount}`;
  if (option.noDataCount === 0) {
    subtext = validDataText;
  } else {
    if (option.key === FieldName.vmDeviceBrand) {
      const ratioText = hasDataPercentageFormatted
        ? ze(`（占比 ${hasDataPercentageFormatted}）`, `(${hasDataPercentageFormatted} of Total)`)
        : '';
      subtext = `${ze('模拟器上报次数', 'Emulator Reports')}: ${hasDataCount}  ${ratioText} \n${t('hardwareInfo.已过滤n条非模拟器上报', { n: option.noDataCount })}`;
    } else {
      subtext = `${validDataText} \n${t('hardwareInfo.已过滤n条无数据上报', { n: option.noDataCount })}`;
    }
  }

  if (option.aggregateType === 0 || option.aggregateType === 2) {
    chartOptions = {
      color: COLOR_LIST,
      title: {
        text: option.label,
        subtext,
        x: 'center',
      },
      tooltip: {
        trigger: 'item',
        formatter: `{b}<br>${t('hardwareInfo.异常次数')}: {c}<br>${t('hardwareInfo.次数占比')}: {d} %`,
        /* formatter: (params) => {
          const { data, percent } = params;
          return `${data.name}<br>次数: ${data.value}<br>占比: ${percent} %`;
        }, */
      },
      series: [{
        type: 'pie',
        radius: '50%',
        data: option.valueList.filter(x => x.visibleInChart).map(x => ({
          value: x.count,
          name: x.isNoData ? t('hardwareInfo.无数据') : x.name,
          isNoData: x.isNoData,
          isShowAsOthers: x.isShowAsOthers,
          ...(x.isNoData && { itemStyle: { color: '#EEE' }, emphasis: { itemStyle: { color: '#EEE' } } }),
          ...(x.isShowAsOthers && { itemStyle: { color: '#8e5933' }, emphasis: { itemStyle: { color: '#8e5933' } } }),
        })),
        label: {
          position: 'outer',
          alignTo: 'edge',
          margin: 10,
        },
      }],
    };
  } else if (option.aggregateType === 1) {
    chartOptions = {
      color: COLOR_LIST,
      title: {
        text: option.label,
        subtext,
        x: 'center',
      },
      grid: {
        left: 60,
      },
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          // console.warn(params);
          const { data } = params;
          const { value: [xValue, yValue], name, reportCount } = data;
          return `${name}<br>${t('hardwareInfo.异常次数')}: ${reportCount}<br>${t('hardwareInfo.次数占比')}: ${Math.round(yValue * 100)} %`;
        },
      },
      xAxis: [{
        name: ze(`${option.label}`, `% of ${option.label}`),
        nameLocation: 'center',
        nameGap: 25,
        type: 'value',
        scale: true,
        axisLabel: {
          formatter: (x) => `${x}%`,
        },
        max: 100,
      }],
      yAxis: [{
        name: ze('上报占比', '% of Reports'),
        type: 'value',
        offset: 10,
        axisLabel: {
          formatter: (y) => `${Math.round(y * 100)}%`,
        },
        max: 1,
      }],
      series: [{
        type: 'bar',
        barWidth: '80%',
        data: refineHistogramData(ze, option),
        label: {
          position: 'outer',
          alignTo: 'edge',
          margin: 10,
        },
      }],
    };
  }

  const anomaly = option.anomaly;
  let anomalyBadge = null;
  if (anomaly && anomaly.kind === 'lift') {
    const issuePct = `${(anomaly.issueRatio * 100).toFixed(1)}%`;
    const basePct = `${(anomaly.baselineRatio * 100).toFixed(1)}%`;
    const liftStr = `${anomaly.lift.toFixed(1)}x`;
    anomalyBadge = (
      <Tag color="volcano" style={{ margin: 0, fontSize: '11px' }}>
        {`⚡ ${ze('高于大盘', 'Above Base')}: ${anomaly.valueName} (${issuePct} vs ${ze('大盘', 'Base')} ${basePct} · ${liftStr})`}
      </Tag>
    );
  } else if (anomaly && anomaly.topBaselineComparison && anomaly.topBaselineComparison.baselineRatio != null) {
    const cmp = anomaly.topBaselineComparison;
    const issuePct = `${(cmp.issueRatio * 100).toFixed(1)}%`;
    const basePct = `${(cmp.baselineRatio * 100).toFixed(1)}%`;
    const liftStr = `${cmp.lift.toFixed(1)}x`;
    anomalyBadge = (
      <Tag
        data-testid={`device-chart-baseline-${option.key}`}
        style={{ margin: 0, fontSize: '11px', color: '#595959', background: '#fafafa', borderColor: '#d9d9d9' }}
      >
        {`${ze('大盘对照', 'Baseline')}: ${cmp.valueName} (${ze('本问题', 'Issue')} ${issuePct} vs ${ze('大盘', 'Base')} ${basePct} · ${liftStr})`}
      </Tag>
    );
  }

  return (
    <div style={{ position: 'relative' }} data-testid={`device-chart-${option.key}`}>
      {anomalyBadge && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2px' }}>
          {anomalyBadge}
        </div>
      )}
      <ReactECharts
        option={chartOptions}
        style={{ width: '100%' }}
        onEvents={{click: (event) => handleClick(event,option)}}
      />
      {/* option.aggregateType !== 1 && <Button
        style={{ position: 'absolute', top: '0px', right: '20px' }}
        icon={<DownloadOutlined />}
        size="small"
        onClick={() => handleDownload(option)}
      >{ t('hardwareInfo.下载') }</Button>*/}
    </div>
  );
};

DeviceStats.propTypes = {
  appId: PropTypes.string,
  platformId: PropTypes.string,
  issueHash: PropTypes.string,
};

const mapStateToProps = state => ({
  reduxState: state,
});

export default connect(mapStateToProps)(DeviceStats);
